import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useStudents } from '@/hooks/useStudents'
import { useInstructorExternalSchools } from '@/hooks/useInstructorExternalSchools'
import { useInstructorSchoolMemberships } from '@/hooks/useInstructorSchoolMemberships'
import { StatusTabs } from '@/components/ui/StatusTabs'
import { Icon, ICONS } from '@/components/ui/Icon'
import { Badge, type BadgeTone } from '@/components/ui/Badge'
import { effectiveInviteStatus, INVITE_TABS, type InviteTabKey } from '@/lib/invitationStatus'

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
}

function schoolLabel(s: { schoolName: string | null; externalSchoolName: string | null }): string {
  if (s.schoolName) return s.schoolName
  if (s.externalSchoolName) return s.externalSchoolName
  return 'Private student'
}

function g1ExpiryLabel(expiryIso: string | null): { text: string; urgent: boolean } {
  if (!expiryIso) return { text: '—', urgent: false }
  const expiry = new Date(expiryIso)
  const now    = new Date()
  const days   = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  if (days < 0)   return { text: 'Expired', urgent: true }
  if (days <= 90) return { text: `Expires ${formatDate(expiryIso)}`, urgent: true }
  return { text: formatDate(expiryIso), urgent: false }
}

// ── Ended students ────────────────────────────────────────────────────────────

function EndedStudentsSection({
  endedStudents, loading, error, onOpen,
}: {
  endedStudents: ReturnType<typeof useStudents>['endedStudents']
  loading: boolean
  error: string | null
  onOpen: (relationshipId: string) => void
}) {
  if (loading) {
    return <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '24px 0' }}>Loading…</div>
  }
  if (error) {
    return <div className='banner-error'>{error}</div>
  }
  if (endedStudents.length === 0) {
    return (
      <div style={{
        background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14,
        padding: '36px 28px', textAlign: 'center',
      }}>
        <div style={{ font: '600 16px Lora, serif', color: '#1B2A4A', marginBottom: 8 }}>No former students</div>
        <div style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
          Students whose relationship with you has ended will appear here, with their lesson and skill history preserved.
        </div>
      </div>
    )
  }

  return (
    <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, overflow: 'hidden' }}>
      {endedStudents.map((s, i) => {
        const isLast = i === endedStudents.length - 1
        return (
          <div
            key={s.relationshipId}
            onClick={() => onOpen(s.relationshipId)}
            style={{
              padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', cursor: 'pointer',
              borderBottom: isLast ? 'none' : '1px solid rgba(27,42,74,0.05)',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ font: '600 13.5px Inter, sans-serif', color: '#1B2A4A' }}>{s.fullName ?? '—'}</div>
              <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 2 }}>
                {s.email} · {formatDate(s.startedAt)} – {formatDate(s.endedAt)}
              </div>
            </div>
            <span style={{ font: '500 12.5px Inter, sans-serif', color: '#0F7C6C', flexShrink: 0 }}>View history →</span>
          </div>
        )
      })}
    </div>
  )
}

// ── Active students table ────────────────────────────────────────────────────

const ACTIVE_COLS = 'minmax(0,1.5fr) minmax(0,1.1fr) minmax(0,1fr) minmax(0,1.1fr) minmax(0,1.1fr) minmax(0,0.9fr) auto'

function ActiveStudentsSection({
  students, loading, error, onOpen, onEnroll,
}: {
  students: ReturnType<typeof useStudents>['students']
  loading: boolean
  error: string | null
  onOpen: (relationshipId: string) => void
  onEnroll: () => void
}) {
  if (loading) {
    return <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '24px 0' }}>Loading…</div>
  }
  if (error) {
    return <div className='banner-error' style={{ marginBottom: 16 }}>{error}</div>
  }
  if (students.length === 0) {
    return (
      <div style={{
        background: '#fff', border: '1px solid rgba(27,42,74,0.08)',
        borderRadius: 14, padding: '36px 28px', textAlign: 'center',
      }}>
        <div style={{ font: '600 16px Lora, serif', color: '#1B2A4A', marginBottom: 8 }}>
          No active students yet
        </div>
        <div style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginBottom: 20 }}>
          Enroll a student and they will appear here once they accept.
        </div>
        <button onClick={onEnroll} className='btn-teal' style={{ width: 'auto', padding: '11px 22px' }}>
          Enroll a student
        </button>
      </div>
    )
  }

  return (
    <>
      <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, overflow: 'hidden' }}>
        <div className='dt-head' style={{ gridTemplateColumns: ACTIVE_COLS }}>
          <span>Student</span>
          <span>School</span>
          <span>Package</span>
          <span>G1 licence</span>
          <span>G1 expiry</span>
          <span>Since</span>
          <span />
        </div>

        {students.map((s, i) => {
          const expiry = g1ExpiryLabel(s.g1ExpiryDate)
          const isLast = i === students.length - 1
          return (
            <div key={s.relationshipId}>
              <div
                className='dt-row-desk'
                onClick={() => onOpen(s.relationshipId)}
                style={{
                  gridTemplateColumns: ACTIVE_COLS,
                  borderBottom: isLast ? 'none' : '1px solid rgba(27,42,74,0.05)',
                  cursor: 'pointer',
                }}
              >
                <div>
                  <div style={{ font: '600 13.5px Inter, sans-serif', color: '#1B2A4A' }}>
                    {s.fullName ?? '—'}
                  </div>
                  <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 2 }}>
                    {s.email}
                  </div>
                </div>
                <div style={{ font: '400 13px Inter, sans-serif', color: s.schoolName || s.externalSchoolName ? '#1B2A4A' : 'rgba(27,42,74,0.4)' }}>
                  {schoolLabel(s)}
                </div>
                <div style={{ font: '400 13px Inter, sans-serif', color: s.packageName ? '#1B2A4A' : 'rgba(27,42,74,0.4)' }}>
                  {s.packageName ?? '—'}
                </div>
                <div style={{ font: '400 13px Inter, sans-serif', color: '#1B2A4A' }}>
                  {s.g1LicenceNumber ?? '—'}
                </div>
                <div style={{ font: '400 13px Inter, sans-serif', color: expiry.urgent ? 'var(--danger)' : '#1B2A4A' }}>
                  {expiry.text}
                </div>
                <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
                  {formatDate(s.startedAt)}
                </div>
                <span style={{ font: '500 13px Inter, sans-serif', color: '#0F7C6C' }}>→</span>
              </div>

              <div
                className='dt-row-mob'
                onClick={() => onOpen(s.relationshipId)}
                style={{ borderBottom: isLast ? 'none' : '1px solid rgba(27,42,74,0.05)', cursor: 'pointer' }}
              >
                <div>
                  <div style={{ font: '600 14px Inter, sans-serif', color: '#1B2A4A' }}>
                    {s.fullName ?? '—'}
                  </div>
                  <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 2 }}>
                    {s.email}
                  </div>
                  <div style={{ font: '400 12px Inter, sans-serif', color: s.schoolName || s.externalSchoolName ? 'rgba(27,42,74,0.5)' : 'rgba(27,42,74,0.35)', marginTop: 2 }}>
                    {schoolLabel(s)}
                  </div>
                  {s.packageName && (
                    <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginTop: 2 }}>
                      Package: {s.packageName}
                    </div>
                  )}
                  {s.g1LicenceNumber && (
                    <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginTop: 2 }}>
                      G1: {s.g1LicenceNumber}
                      {expiry.urgent && (
                        <span style={{ color: 'var(--danger)', marginLeft: 6 }}>· {expiry.text}</span>
                      )}
                    </div>
                  )}
                </div>
                <span style={{ font: '500 13px Inter, sans-serif', color: '#0F7C6C', flexShrink: 0 }}>→</span>
              </div>
            </div>
          )
        })}
      </div>

      <div style={{ marginTop: 14, font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', textAlign: 'right' }}>
        {students.length} student{students.length !== 1 ? 's' : ''}
      </div>
    </>
  )
}

// ── Invites tab (merged from the former standalone Enroll page) ─────────────

const PRIVATE_SCHOOL = '__private__'
// Prefix distinguishes a registered-school choice (schoolId, migration 065) from a
// private external-school label (externalSchoolId, migration 058) in the single <select>
// below — the two are mutually exclusive at the database layer
// (chk_invitations_school_mutually_exclusive) and must never be sent as the same id.
const REGISTERED_PREFIX = 'registered:'

interface InvitationRow {
  id: string
  invited_email: string | null
  status: string
  expires_at: string
  accepted_at: string | null
  created_at: string
}

const STATUS_TONE: Record<string, BadgeTone> = {
  pending: 'warning', accepted: 'success', revoked: 'danger', expired: 'neutral',
}

function EnrollForm({ onGenerated }: { onGenerated: () => void }) {
  const { user, profile } = useAuth()
  const { schools: externalSchools, loading: schoolsLoading } = useInstructorExternalSchools()
  const { schools: registeredSchools, loading: registeredLoading } = useInstructorSchoolMemberships()
  // An instructor who is an EMPLOYEE at any school is presumed to be working under that
  // school for every student they enroll — they cannot send a "private" or
  // externally-labeled invite at all, only attribute to one of their employer school(s).
  // A contractor (or an instructor with no employee membership at all) keeps full choice.
  // Product decision, not a technical constraint: see migration 067's header.
  const employeeSchools = registeredSchools.filter(s => s.relationshipType === 'employee')
  const isForcedToEmployer = employeeSchools.length > 0

  const [email,        setEmail]        = useState('')
  const [schoolChoice, setSchoolChoice] = useState(PRIVATE_SCHOOL)
  const [generating,   setGenerating]   = useState(false)
  const [newCode,      setNewCode]      = useState<string | null>(null)
  const [copied,       setCopied]       = useState(false)
  const [genError,     setGenError]     = useState('')

  useEffect(() => {
    if (isForcedToEmployer && (schoolChoice === PRIVATE_SCHOOL || schoolChoice === '')) {
      setSchoolChoice(employeeSchools.length === 1 ? `${REGISTERED_PREFIX}${employeeSchools[0].schoolId}` : '')
    }
  }, [isForcedToEmployer, employeeSchools, schoolChoice])

  const handleGenerate = async () => {
    if (generating || !user) return
    setGenError(''); setNewCode(null); setCopied(false)
    if (!email.trim()) { setGenError('Student email is required.'); return }
    if (isForcedToEmployer && !schoolChoice.startsWith(REGISTERED_PREFIX)) {
      setGenError('Select which of your schools this student belongs to.')
      return
    }
    setGenerating(true)
    try {
      // The selected school is sent as a hint only — forgery is not possible here:
      // send-invite independently re-verifies, using the caller's own verified identity,
      // ownership/active status for an externalSchoolId (instructor_external_schools) or
      // active membership for a schoolId (school_memberships) before ever persisting
      // either value. See that function's own comments. The two are mutually exclusive —
      // the REGISTERED_PREFIX on schoolChoice is what this form uses to tell them apart.
      const isRegistered = schoolChoice.startsWith(REGISTERED_PREFIX)
      const schoolId = isRegistered ? schoolChoice.slice(REGISTERED_PREFIX.length) : null
      const externalSchoolId = !isRegistered && schoolChoice !== PRIVATE_SCHOOL ? schoolChoice : null
      const { data, error } = await supabase.functions.invoke('send-invite', {
        body: { studentEmail: email.trim(), instructorName: profile?.full_name ?? '', externalSchoolId, schoolId },
      })

      if (error) { setGenError('Could not send invite. Please try again.'); return }
      if (data?.error) { setGenError(data.error); return }
      if (data?.warning) { setGenError(data.warning) }

      setNewCode(data.token)
      setEmail('')
      if (!isForcedToEmployer) setSchoolChoice(PRIVATE_SCHOOL)
      onGenerated()
    } finally {
      setGenerating(false)
    }
  }

  const handleCopy = () => {
    if (!newCode) return
    navigator.clipboard.writeText(newCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, padding: '22px 22px', marginBottom: 20 }}>
      <div style={{ font: '600 15px Inter, sans-serif', color: '#1B2A4A', marginBottom: 14 }}>
        New invite code
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 220px' }}>
          <label style={{ font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', display: 'block', marginBottom: 6 }}>
            Student email
          </label>
          <input
            type='email'
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder='student@example.com'
            className='field-input'
            required
          />
        </div>
        <div style={{ flex: '1 1 220px' }}>
          <label style={{ font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', display: 'block', marginBottom: 6 }}>
            School
          </label>
          <select
            value={schoolChoice}
            onChange={e => setSchoolChoice(e.target.value)}
            className='field-input'
            disabled={schoolsLoading || registeredLoading}
          >
            {isForcedToEmployer ? (
              <>
                <option value='' disabled>Select your school…</option>
                <optgroup label='Your school'>
                  {employeeSchools.map(s => (
                    <option key={s.schoolId} value={`${REGISTERED_PREFIX}${s.schoolId}`}>{s.schoolName}</option>
                  ))}
                </optgroup>
              </>
            ) : (
              <>
                <option value={PRIVATE_SCHOOL}>Private student / No school</option>
                {registeredSchools.length > 0 && (
                  <optgroup label='Registered schools (they can view this student)'>
                    {registeredSchools.map(s => (
                      <option key={s.schoolId} value={`${REGISTERED_PREFIX}${s.schoolId}`}>{s.schoolName}</option>
                    ))}
                  </optgroup>
                )}
                {externalSchools.filter(s => s.status === 'active').length > 0 && (
                  <optgroup label='My private school labels (not registered on DriveTrack)'>
                    {externalSchools.filter(s => s.status === 'active').map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </optgroup>
                )}
              </>
            )}
          </select>
          {isForcedToEmployer && (
            <div style={{ font: '400 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 6 }}>
              You're an employee at {employeeSchools.length === 1 ? employeeSchools[0].schoolName : 'these schools'} — students you enroll are attributed there.
            </div>
          )}
        </div>
      </div>

      {genError && <div className='banner-error' style={{ marginBottom: 12 }}>{genError}</div>}

      <button className='btn-teal' onClick={handleGenerate} disabled={generating} style={{ width: 'auto', padding: '11px 22px' }}>
        {generating ? 'Generating…' : 'Generate invite code'}
      </button>

      {newCode && (
        <div style={{ marginTop: 18, padding: '16px 18px', background: 'rgba(15,124,108,0.07)', border: '1px solid rgba(15,124,108,0.2)', borderRadius: 11 }}>
          <div style={{ font: '600 12px Inter, sans-serif', color: '#0F7C6C', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Share this code with your student
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              flex: 1, font: '600 15px/1 "Courier New", monospace', color: '#1B2A4A',
              background: '#fff', border: '1px solid rgba(27,42,74,0.12)', borderRadius: 8,
              padding: '11px 14px', wordBreak: 'break-all',
            }}>
              {newCode}
            </div>
            <button
              onClick={handleCopy}
              style={{
                padding: '11px 16px', background: copied ? '#0F7C6C' : '#1B2A4A',
                border: 'none', borderRadius: 8, font: '600 13px Inter, sans-serif',
                color: '#fff', cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s',
              }}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginTop: 10 }}>
            Expires in 7 days. The student enters this on the registration page.
          </div>
        </div>
      )}
    </div>
  )
}

function InvitesTab({ formOpen, onToggleForm }: { formOpen: boolean; onToggleForm: () => void }) {
  const { user } = useAuth()
  const [invitations, setInvitations] = useState<InvitationRow[]>([])
  const [loadingList, setLoadingList] = useState(true)
  const [activeTab,   setActiveTab]   = useState<InviteTabKey>('pending')
  const [revoking,    setRevoking]    = useState<string | null>(null)
  const [revokeError, setRevokeError] = useState('')

  const fetchInvitations = useCallback(async () => {
    if (!user) return
    setLoadingList(true)
    const { data } = await supabase
      .from('invitations')
      .select('id, invited_email, status, expires_at, accepted_at, created_at')
      .eq('instructor_id', user.id)
      .order('created_at', { ascending: false })
    setInvitations((data as InvitationRow[]) ?? [])
    setLoadingList(false)
  }, [user])

  useEffect(() => { fetchInvitations() }, [fetchInvitations])

  const handleRevoke = async (id: string) => {
    setRevoking(id)
    setRevokeError('')
    // Only send the status transition — revoked_at is set server-side by
    // the invitations_set_revoked_at trigger (migration 022).
    const { error, count } = await supabase
      .from('invitations')
      .update({ status: 'revoked' }, { count: 'exact' })
      .eq('id', id)

    setRevoking(null)

    if (error) {
      setRevokeError('Could not revoke this invite. Please try again.')
      return
    }
    // RLS can return a successful response that matched zero rows (e.g. the invite was
    // already accepted or revoked by the time this request landed) — PostgREST's 204/200
    // is not proof the row actually changed, so this is checked explicitly rather than
    // assumed from the lack of an error.
    if (!count) {
      setRevokeError('This invite could no longer be revoked — it may have just been accepted or already revoked.')
    }
    fetchInvitations()
  }

  const tabCounts = INVITE_TABS.map(t => ({
    ...t,
    count: invitations.filter(inv => effectiveInviteStatus(inv.status, inv.expires_at) === t.key).length,
  }))
  const visibleInvitations = invitations.filter(inv => effectiveInviteStatus(inv.status, inv.expires_at) === activeTab)

  return (
    <div>
      {formOpen && <EnrollForm onGenerated={fetchInvitations} />}

      <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ padding: '16px 22px 0', font: '600 15px Inter, sans-serif', color: '#1B2A4A', marginBottom: 2 }}>
          Sent invites
        </div>
        <StatusTabs tabs={tabCounts} active={activeTab} onChange={setActiveTab} />

        {revokeError && (
          <div className='banner-error' style={{ margin: '14px 22px 0' }}>{revokeError}</div>
        )}

        {loadingList ? (
          <div style={{ padding: '24px 22px', font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>Loading…</div>
        ) : visibleInvitations.length === 0 ? (
          <div style={{ padding: '24px 22px', font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>
            {invitations.length === 0 ? (
              <>No invites yet. {!formOpen && (
                <button onClick={onToggleForm} style={{ background: 'none', border: 'none', color: '#0F7C6C', cursor: 'pointer', font: '500 13px Inter, sans-serif', padding: 0 }}>
                  Generate one above.
                </button>
              )}</>
            ) : `No ${INVITE_TABS.find(t => t.key === activeTab)?.label.toLowerCase()} invites.`}
          </div>
        ) : (
          visibleInvitations.map(inv => {
            const status = effectiveInviteStatus(inv.status, inv.expires_at)
            return (
              <div key={inv.id} style={{
                padding: '14px 22px', borderBottom: '1px solid rgba(27,42,74,0.05)',
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ font: '500 13.5px Inter, sans-serif', color: '#1B2A4A' }}>
                    {inv.invited_email ?? '—'}
                  </div>
                  <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 2 }}>
                    Created {formatDate(inv.created_at)}
                    {inv.accepted_at ? ` · Accepted ${formatDate(inv.accepted_at)}` : ` · Expires ${formatDate(inv.expires_at)}`}
                  </div>
                </div>

                <Badge label={INVITE_TABS.find(t => t.key === status)?.label ?? status} tone={STATUS_TONE[status] ?? 'neutral'} />

                {status === 'pending' && (
                  <button
                    onClick={() => handleRevoke(inv.id)}
                    disabled={revoking === inv.id}
                    style={{
                      padding: '5px 12px', background: 'transparent',
                      border: '1px solid var(--danger-25)', borderRadius: 7,
                      font: '500 12px Inter, sans-serif', color: 'var(--danger)',
                      cursor: 'pointer', flexShrink: 0,
                    }}
                  >
                    {revoking === inv.id ? '…' : 'Revoke'}
                  </button>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

type Tab = 'active' | 'ended' | 'invites'

export default function Students() {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    students, loading, error,
    endedStudents, loadingEnded, errorEnded, fetchEndedStudents,
  } = useStudents()

  const navState = location.state as { tab?: Tab; openForm?: boolean } | null
  const [tab, setTab] = useState<Tab>(navState?.tab ?? 'active')
  const [formOpen, setFormOpen] = useState(!!navState?.openForm)

  const handleTabChange = (next: Tab) => {
    setTab(next)
    // Lazy: the ended list is never fetched until the instructor actually opens this tab.
    if (next === 'ended') fetchEndedStudents()
  }

  const openStudent = (relationshipId: string) => navigate(`/app/instructor/students/${relationshipId}`)

  const openEnroll = () => {
    setTab('invites')
    setFormOpen(true)
  }

  const TABS: { key: Tab; label: string }[] = [
    { key: 'active',  label: 'Active' },
    { key: 'ended',   label: 'Ended' },
    { key: 'invites', label: 'Invites' },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, marginBottom: 6 }}>
        <div>
          <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A' }}>Students</div>
          <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginTop: 6 }}>
            Your students, from invite to active lessons.
          </div>
        </div>
        <button
          onClick={tab === 'invites' ? () => setFormOpen(v => !v) : openEnroll}
          className={tab === 'invites' && formOpen ? '' : 'btn-teal'}
          style={{
            width: 'auto', padding: '10px 18px', flexShrink: 0,
            display: 'inline-flex', alignItems: 'center', gap: 7,
            ...(tab === 'invites' && formOpen
              ? { background: 'rgba(27,42,74,0.06)', border: 'none', borderRadius: 8, color: '#1B2A4A', cursor: 'pointer', font: '600 13px Inter, sans-serif' }
              : {}),
          }}
        >
          <Icon path={tab === 'invites' && formOpen ? ICONS.close : ICONS.plus} size={14} />
          {tab === 'invites' && formOpen ? 'Cancel' : 'Enroll a student'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 4, marginTop: 24, marginBottom: 24, borderBottom: '1px solid rgba(27,42,74,0.08)' }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => handleTabChange(t.key)}
            style={{
              padding: '10px 4px', marginRight: 20, background: 'none', border: 'none',
              borderBottom: tab === t.key ? '2px solid #0F7C6C' : '2px solid transparent',
              font: tab === t.key ? '600 13.5px Inter, sans-serif' : '500 13.5px Inter, sans-serif',
              color: tab === t.key ? '#1B2A4A' : 'rgba(27,42,74,0.5)', cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'active' && (
        <ActiveStudentsSection students={students} loading={loading} error={error} onOpen={openStudent} onEnroll={openEnroll} />
      )}
      {tab === 'ended' && (
        <EndedStudentsSection endedStudents={endedStudents} loading={loadingEnded} error={errorEnded} onOpen={openStudent} />
      )}
      {tab === 'invites' && (
        <InvitesTab formOpen={formOpen} onToggleForm={() => setFormOpen(v => !v)} />
      )}
    </div>
  )
}
